'use client';

import { useState } from 'react';
import { Check, Link2 } from 'lucide-react';
import { FaFacebookF, FaLinkedinIn, FaXTwitter } from 'react-icons/fa6';

import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Zdieľanie článku — Figma desktop `60034:2761`, mobil `60042:7522`.
 *
 * Popisok „Zdieľať článok" a štyri štvorcové tlačidlá 52×52 s medzerou 8:
 * kopírovanie odkazu, Facebook, LinkedIn, X. Na desktope je popisok vľavo
 * a tlačidlá vpravo od neho, na mobile sú pod ním.
 *
 * Značkové ikony berieme z `react-icons/fa6` — `lucide-react` v1 ich už
 * neobsahuje (rovnako ako v pätke).
 *
 * Farby: jediná premenná je `primary`, zvyšok natvrdo hexom.
 */
export function ShareArticle({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);

  const share = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    x: `https://x.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Schránka môže byť zakázaná (staršie prehliadače, nezabezpečený pôvod) —
      // vtedy sa proste nič nestane, radšej než hádzať chybu do konzoly.
    }
  };

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-8">
      <span className="text-[14px] leading-[1.5] tracking-[-0.031em] text-[#212121] sm:text-[15px] lg:text-[16px]">
        Zdieľať článok
      </span>

      <div className="flex gap-2">
        <button type="button" onClick={copyLink} aria-label="Skopírovať odkaz" className={BOX}>
          {copied ? (
            <Check className="size-6 text-primary" strokeWidth={2} aria-hidden="true" />
          ) : (
            <Link2 className="size-6" strokeWidth={2} aria-hidden="true" />
          )}
        </button>

        <ShareLink href={share.facebook} label="Zdieľať na Facebooku">
          <FaFacebookF className="size-5" aria-hidden="true" />
        </ShareLink>
        <ShareLink href={share.linkedin} label="Zdieľať na LinkedIne">
          <FaLinkedinIn className="size-5" aria-hidden="true" />
        </ShareLink>
        <ShareLink href={share.x} label="Zdieľať na X">
          <FaXTwitter className="size-5" aria-hidden="true" />
        </ShareLink>
      </div>
    </div>
  );
}

const BOX = cn(
  'flex size-11 shrink-0 items-center justify-center rounded-8 lg:size-[52px]',
  'border border-[#DDE3EC] bg-[#FFFFFF] text-[#212121]',
  'transition-colors hover:border-primary hover:bg-primary hover:text-[#FFFFFF]',
);

function ShareLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className={BOX}>
      {children}
    </a>
  );
}
